/// <reference types="@types/googlemaps" />
import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable, ObservableInput, throwError } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { GoogleMapApiClient } from '@nimbus-library/features/shared/services/google-map/google-map.api-client';
import { IGoogleAddressResponse } from '@nimbus-library/features/shared/models/google-maps/i-google-maps.model';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { CacheService, IEntityCacheService } from '@nimbus-library/features/cache/index';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';
import * as gmapThemeStyles from './google-map.styles.json';

declare let google: any;

@Injectable({ providedIn: 'root' })
export class GoogleMapService {
  private gmapStatus$: BehaviorSubject<boolean> = new BehaviorSubject(undefined);

  private apiKey: string;
  private readonly gmapStyles;
  private readonly googleMapAddressCache: IEntityCacheService<IGoogleAddressResponse>;

  public constructor(
    private readonly logger: ILogger,
    private readonly googleMapApiClient: GoogleMapApiClient,
    cacheService: CacheService,
    environmentConfigurationService: EnvironmentConfigurationService,
  ) {
    this.apiKey = environmentConfigurationService.configurationSettings.googleApiKey || process.env.GOOGLE_MAPS_KEY;
    this.gmapStyles = gmapThemeStyles.default;
    this.googleMapAddressCache = cacheService.for<IGoogleAddressResponse>('GoogleMapAddressCache', {
      preloadMaxAge: moment.duration(12, 'hour'),
    });
  }

  public load(): Observable<boolean> {
    if (this.gmapStatus$.getValue() === undefined) {
      (<any>window).googleMapsReady = () => {
        this.gmapStatus$.next(true);
        delete (<any>window).googleMapsReady;
      };

      this.addGoogleMapScript();

      this.gmapStatus$.next(false);
    }
    return this.gmapStatus$.pipe(filter((val) => val === true));
  }

  public createMap(gmapElement: HTMLElement, location: IGoogleAddressResponse, theme: ChartTheme): ObservableInput<void> {
    return this.renderMap(gmapElement, location, theme);
  }

  public findLocation(address: string, fallbackAddress: string): Observable<IGoogleAddressResponse> {
    return this.googleMapApiClient.getAddress(address, false).pipe(
      this.googleMapAddressCache.useCache(address),
      catchError((error1) => {
        this.logger.warn(error1);
        return this.googleMapApiClient.getAddress(fallbackAddress, true).pipe(
          this.googleMapAddressCache.useCache(fallbackAddress),
          catchError((error2) => {
            this.logger.warn(error2);
            return throwError(error2);
          }),
        );
      }),
    );
  }

  private renderMap(gmapElement: HTMLElement, location: IGoogleAddressResponse, theme: ChartTheme) {
    const position: { lat: number; lng: number } = new google.maps.LatLng(location.lat, location.lng);
    let stylesArray: [];
    switch (theme) {
      case ChartTheme.Dark:
        stylesArray = this.gmapStyles.dark;
        break;
      case ChartTheme.HighContrast:
        stylesArray = this.gmapStyles.contrast;
        break;
      default:
        stylesArray = this.gmapStyles.default;
    }

    const map = new google.maps.Map(gmapElement, {
      center: position,
      zoom: 15,
      minZoom: 1,
      maxZoom: 15,
      styles: stylesArray,
    });

    map.setCenter({ lat: location.lat, lng: location.lng });
    const marker = new google.maps.Marker({
      position,
      title: 'Here I am!',
      animation: google.maps.Animation.DROP,
    });
    marker.setMap(map);
    return map;
  }

  private removeGoogleMapScript(): void {
    const keywords: string[] = ['maps.googleapis', 'googleMapsReady'];

    // Remove google from BOM (window object).
    // Important! Elsewhere will cause the error: 154 You have included the Google Maps JavaScript API multiple times on this page.
    window.google = undefined;

    // Remove google map scripts from DOM
    const scripts = document.head.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
      const scriptSource = scripts[i].getAttribute('src');
      if (scriptSource != null) {
        if (keywords.filter((item) => scriptSource.includes(item)).length) {
          scripts[i].remove();
        }
      }
    }
  }

  private addGoogleMapScript(): void {
    this.removeGoogleMapScript();

    const googleApiString = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&callback=googleMapsReady`;
    const headContent: HTMLElement = document.head;
    const script: HTMLElementTagNameMap['script'] = document.createElement('script');

    script.src = googleApiString;
    script.type = 'text/javascript';
    script.defer = true;
    script.async = false;
    headContent.appendChild(script);
  }
}
